import { defineComponent as _defineComponent } from 'vue';
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = { key: 0 };
const _hoisted_2 = ["media", "srcset"];
const _hoisted_3 = ["alt", "src", "loading"];
import { computed } from 'vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'SinglePicture',
    props: {
        image: {},
        extraClasses: {},
        forcedRatio: { type: Boolean, default: true },
        allowCaption: { type: Boolean, default: false },
        useJpFont: { type: Boolean, default: false },
        lazyLoadImage: { default: 'lazy' },
        isChildPageHeader: { type: Boolean, default: false },
        imageClasses: { default: '' },
        sizes: { default: undefined }
    },
    setup(__props) {
        const props = __props;
        // WordPress size map with logical widths
        const wordpressSizeMap = [
            { name: 'thumbnail', width: 150 },
            { name: 'medium', width: 300 },
            { name: 'medium_large', width: 768 },
            { name: 'large', width: 1024 },
            { name: '1536x1536', width: 1536 },
            { name: '2048x2048', width: 2048 },
            { name: 'full', width: Infinity },
        ];
        const tailwindScreens = {
            initial: '0rem',
            '2xs': '20rem',
            xs: '25rem',
            sm: '30rem',
            md: '45rem',
            lg: '64rem',
            xl: '80rem',
            '2xl': '90rem',
            '3xl': '120rem',
        };
        /**
         * Check if the image has a valid URL.
         */
        const hasImage = computed(() => {
            return !!props.image?.url;
        });
        const defaultSrc = computed(() => {
            const sources = generatedSources.value || [];
            return sources?.at(-1)?.src || props.image.url || '';
        });
        /**
         * Get the best WordPress image URL for the target width deterministically.
         *
         * @param {number} targetWidth - The target pixel width to match.
         * @returns {string} - The URL of the selected WordPress image size.
         */
        const getSrcForTargetWidth = (targetWidth) => {
            for (const sizeEntry of wordpressSizeMap) {
                if (targetWidth <= sizeEntry.width &&
                    props.image.size_urls?.[sizeEntry.name]) {
                    return props.image.size_urls[sizeEntry.name];
                }
            }
            return props.image.size_urls?.['full'] || props.image.url || '';
        };
        /**
         * Generates <source> elements for the <picture> tag with correct media queries and srcset.
         * Reverses order so larger breakpoints are first for correct browser priority.
         */
        const generatedSources = computed(() => {
            if (!props.sizes)
                return [];
            return Object.entries(props.sizes)
                .sort((a, b) => {
                const aPx = parseFloat(tailwindScreens[a[0]]) * 16;
                const bPx = parseFloat(tailwindScreens[b[0]]) * 16;
                return bPx - aPx; // descending order
            })
                .map(([screenKey, widthPx]) => {
                const minWidthRem = tailwindScreens[screenKey];
                const minWidthPx = parseFloat(minWidthRem) * 16;
                const targetWidth = parseInt(widthPx.replace('px', ''), 10);
                const src = getSrcForTargetWidth(targetWidth);
                return {
                    media: `(min-width: ${minWidthPx}px)`,
                    src,
                };
            });
        });
        /**
         * Check if the caption should be displayed.
         */
        const displayCaption = computed(() => {
            return !!props.image?.caption && props.allowCaption;
        });
        /**
         * Generate Tailwind classes for the image using spread syntax for clarity.
         */
        const imageClasses = computed(() => {
            return [
                ...(props.imageClasses ? [props.imageClasses] : []),
                ...(props.forcedRatio
                    ? ['aspect-video', 'h-full', 'object-cover']
                    : ['h-auto']),
            ].join(' ');
        });
        /**
         * Compute Japanese or serif font for the caption.
         */
        const displayJapaneseFont = computed(() => {
            return props.useJpFont ? 'font-jp' : 'font-serif';
        });
        return (_ctx, _cache) => {
            return (hasImage.value)
                ? (_openBlock(), _createElementBlock("figure", _hoisted_1, [
                    _createElementVNode("picture", {
                        class: _normalizeClass([__props.extraClasses, "block w-full overflow-hidden"])
                    }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(generatedSources.value, (mediaQuery, i) => {
                            return (_openBlock(), _createElementBlock("source", {
                                key: i,
                                media: mediaQuery.media,
                                srcset: mediaQuery.src
                            }, null, 8, _hoisted_2));
                        }), 128)),
                        _createElementVNode("img", {
                            alt: __props.image.alt,
                            src: defaultSrc.value,
                            class: _normalizeClass([imageClasses.value, "w-full scale-100 transform transition group-hover:scale-105"]),
                            loading: __props.lazyLoadImage
                        }, null, 10, _hoisted_3)
                    ], 2),
                    (displayCaption.value)
                        ? (_openBlock(), _createElementBlock("figcaption", {
                            key: 0,
                            class: _normalizeClass([[
                                    displayJapaneseFont.value,
                                    { absolute: __props.isChildPageHeader, relative: !__props.isChildPageHeader },
                                ], "block text-[16px] leading-[30px]"])
                        }, _toDisplayString(__props.image.caption), 3))
                        : _createCommentVNode("", true)
                ]))
                : _createCommentVNode("", true);
        };
    }
});
